import { useGetMyAccountUsersExcludedDeactivated } from '@/api/queries/me/useGetMyAccountUsersExcludedDeactivated';
import { AnchorButton, ButtonRadioGroup } from '@payaca/ambrosia';
import ErrorBoundary from '@payaca/components/errorBoundary/ErrorBoundary';

import { AccountsPermissions } from '@payaca/permissions/accounts/accounts.permissions';
import { DispatchPermissions } from '@payaca/permissions/dispatch/dispatch.permissions';
import { ScheduledEventsPermissions } from '@payaca/permissions/scheduledEvents/scheduled-events.permissions';
import { FC, useContext, useMemo } from 'react';
import { Link, useRouteMatch } from 'react-router-dom';
import AccountUserAvatarSelector, {
  UNASSIGNED_USER_ID_SENTINEL,
} from '../accountUserAvatarSelector/AccountUserAvatarSelector';
import AddressLookupControl from '../addressLookupControl/AddressLookupControl';
import { ScheduleContext } from '../contextProviders/ScheduleContextProvider';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import ScheduleFilters from '../scheduleFilters/ScheduleFilters';

const UNASSIGNED_NUMERIC_SENTINEL = -1;

const ScheduleDispatchFilters: FC = () => {
  const { url } = useRouteMatch();
  const requestData = useContext(ScheduleContext).requestData;
  const displayType = useContext(ScheduleContext).displayType;
  const hideCreateEventButton =
    useContext(ScheduleContext).hideCreateEventButton;

  const onUpdate = useContext(ScheduleContext).onUpdate;

  const { data: accountUsers = [], isLoading: isLoadingAccountUsers } =
    useGetMyAccountUsersExcludedDeactivated();

  const selectedPublicIds = useMemo(
    () =>
      (requestData.assignedUserIds ?? []).map((numericId) => {
        if (numericId === UNASSIGNED_NUMERIC_SENTINEL) {
          return UNASSIGNED_USER_ID_SENTINEL;
        }
        return (
          accountUsers.find((u) => u.id === String(numericId))?.publicId ?? ''
        );
      }),
    [requestData.assignedUserIds, accountUsers]
  );

  const handleSelectionChange = (nextPublicIds: string[]) => {
    if (isLoadingAccountUsers) return;
    const nextNumericIds = nextPublicIds
      .map<number | null>((publicId) => {
        if (publicId === UNASSIGNED_USER_ID_SENTINEL) {
          return UNASSIGNED_NUMERIC_SENTINEL;
        }
        const matchedId = accountUsers.find((u) => u.publicId === publicId)?.id;
        return matchedId != null ? Number(matchedId) : null;
      })
      .filter((id): id is number => id !== null);
    onUpdate({ selectedUserIds: nextNumericIds });
  };

  return (
    <div>
      <PermissionGuard
        renderIfHasPermissions={[
          ScheduledEventsPermissions.GET_MY_DEAL_EVENTS,
          ScheduledEventsPermissions.GET_EVENTS,
          DispatchPermissions.GET_EVENTS,
          DispatchPermissions.PREVIEW_DISPATCH,
          ScheduledEventsPermissions.ADD_EVENT,
          ScheduledEventsPermissions.ADD_MY_EVENT,
        ]}
      >
        <div className="mb-4 flex flex-wrap gap-8">
          <div className="mr-auto">
            <PermissionGuard
              renderIfHasPermissions={[
                ScheduledEventsPermissions.GET_MY_DEAL_EVENTS,
                ScheduledEventsPermissions.GET_EVENTS,
              ]}
            >
              <PermissionGuard
                renderIfHasPermissions={[AccountsPermissions.GET_USERS]}
              >
                <AccountUserAvatarSelector
                  selectedUserIds={selectedPublicIds}
                  onChange={handleSelectionChange}
                  includeUnassigned
                  disabled={isLoadingAccountUsers}
                />
              </PermissionGuard>
            </PermissionGuard>
          </div>
          <PermissionGuard
            renderIfHasPermissions={[
              DispatchPermissions.PREVIEW_DISPATCH,
              DispatchPermissions.GET_EVENTS,
            ]}
          >
            <ButtonRadioGroup
              value={displayType}
              onChange={(displayType) => {
                onUpdate({ displayType, location: null });
              }}
              options={[
                { label: 'Map', value: 'map' },
                { label: 'Calendar', value: 'calendar' },
              ]}
              sizeVariant={'sm'}
            />
          </PermissionGuard>
          {displayType === 'calendar' && !hideCreateEventButton && (
            <>
              <PermissionGuard
                renderIfHasPermissions={[
                  ScheduledEventsPermissions.ADD_EVENT,
                  ScheduledEventsPermissions.ADD_MY_EVENT,
                ]}
              >
                <AnchorButton
                  as={Link}
                  to={`${url}/new`}
                  colour={'blue'}
                  size={'sm'}
                >
                  Create Event
                </AnchorButton>
              </PermissionGuard>
            </>
          )}
          {displayType === 'map' && (
            <PermissionGuard
              renderIfHasPermissions={[
                ScheduledEventsPermissions.ADD_EVENT,
                ScheduledEventsPermissions.ADD_MY_EVENT,
              ]}
            >
              <AddressLookupControl
                onChange={(change) =>
                  onUpdate({
                    location: {
                      line1: change.line1,
                      line2: change.line2,
                      city: change.city,
                      country: change.country,
                      postcode: change.postcode,
                    },
                  })
                }
                placeholder="Location search"
              />
            </PermissionGuard>
          )}
        </div>
      </PermissionGuard>
      <ErrorBoundary>
        <ScheduleFilters />
      </ErrorBoundary>
    </div>
  );
};
export default ScheduleDispatchFilters;
